import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Loader, Center, Stack, Text } from '@mantine/core';
import {
  useIsMineClient,
  useIsOrganizationMember,
} from '../../../application/store/authStore';
import {
  CLIENT_ROUTES,
  MINEMINER_ROUTES,
  ORGANIZATION_ROUTES,
  PUBLIC_ROUTES,
} from '../../routing/routes';

/**
 * HomeRedirect — the apex `/` route's auth-aware redirect.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). The MineMiner SPA owns
 * `mymineminer.com/` and has no public marketing surface — `/` redirects
 * authenticated reps into the app and everyone else to `/login`.
 *
 * sprint-124 (epic-019 Sprint C). For the Elko Mining Expo the authenticated landing was
 * temporarily the active show's Event detail page (booth immediacy).
 * sprint-173 (TECH_DEBT #127): reverted post-show — authenticated reps now land on Account
 * Intelligence (the recurring executive briefing), the app's default surface.
 */
export const HomeRedirect: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isLoading, isMineMinerRep } = useAuth();
  const isMineClient = useIsMineClient();
  const isOrganizationMember = useIsOrganizationMember();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (isAuthenticated) {
    // sprint-217 (epic-036 Phase 1 increment 1D) — the apex now has two destinations.
    //
    // Staff first: an internal admin who also holds a client role belongs in the staff tool,
    // which is the same precedence `primaryUserRole` applies. Sending a client user to
    // Account Intelligence would bounce them straight off MineMinerRoute into
    // /access-denied — a signed-in user being told they are not allowed at their own front
    // door, which is what happened before this branch existed.
    if (!isMineMinerRep && isMineClient) {
      return <Navigate to={CLIENT_ROUTES.CLIENT_MINES} replace />;
    }

    // sprint-239 (epic-039 Canary increment A) — a third destination, for the same reason the
    // second one exists: a Sync user sent to Account Intelligence would bounce straight off
    // MineMinerRoute into /access-denied, which is a signed-in customer being told they are not
    // allowed at their own front door. Ordered after the mine-client branch to match
    // `primaryUserRole`'s precedence. sprint-250/S003 (epic-039/DR-015): a staff account CAN now
    // also hold an org membership via the explicit dual-lane acknowledgment — such a principal is
    // staff-first here by the same precedence, landing on Account Intelligence with the org area
    // reachable through navigation, which keeps their existing experience unchanged.
    if (!isMineMinerRep && isOrganizationMember) {
      return <Navigate to={ORGANIZATION_ROUTES.ORG_BASE} replace />;
    }

    return <Navigate to={MINEMINER_ROUTES.ACCOUNT_INTELLIGENCE} replace />;
  }

  return <Navigate to={PUBLIC_ROUTES.LOGIN} replace />;
};
